import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglassHalf } from '@fortawesome/sharp-regular-svg-icons';
import { faCircleCheck, faLock, faTimer } from '@fortawesome/sharp-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { convertTime } from '../../shared/util/helperfunctions';

type ProgrammeDay = {
  chid: string;
  title: string;
  completed?: boolean;
  progress?: number;
  thumbnail_image?: string;
  length?: number;
  description?: string;
};

type ProgrammeBlockProps = {
  programme: {
    chid: string;
    progress?: number;
    minutes_watched?: number;
    days?: ProgrammeDay[];
  };
  activeDay: {
    chid?: string;
    title?: string;
  };
};

export function ProgrammeBlock({ programme, activeDay }: ProgrammeBlockProps) {
  const { t } = useTranslation();

  const getIcon = (item: ProgrammeDay) => {
    if (item?.completed) return faCircleCheck;
    if (item?.chid === activeDay?.chid) return faTimer;
    return faLock;
  };

  const getIsAllowed = (index: number) => {
    const activeIndex = programme.days.findIndex(
      (day: ProgrammeDay) => activeDay.title === day.title
    );
    return (activeIndex > -1 && index <= activeIndex) || programme.days[index].progress === 100;
  };

  const renderProgressGraph = (
    icon: IconDefinition,
    value: number,
    labelKey1: string,
    labelKey2: string,
    unitKey: string
  ) => (
    <div className="progress-graph">
      <span className="srOnly">
        {`${t(labelKey1)} ${t(labelKey2)}, ${value.toFixed()} ${t(unitKey)}`}
      </span>
      <div className="progress-circle" aria-hidden="true">
        <div className="circle-text">
          <FontAwesomeIcon icon={icon} />
          <span aria-hidden="true">{value.toFixed()}</span>
          <div className="sml">{t(unitKey)}</div>
        </div>
      </div>
      <div className="progress-text" aria-hidden="true">
        {t(labelKey1)} {t(labelKey2)}
      </div>
    </div>
  );

  return (
    <div className="author prog">
      <section className="library prog" aria-labelledby="programme-days-heading">
        <h2 id="programme-days-heading">{t('M034')}</h2>
        <div className="scrollable">
          {programme.days?.map((item: ProgrammeDay, index: number) => {
            const isAllowed = getIsAllowed(index);

            return (
              <Link
                key={item.title}
                to={`/programme/${programme.chid}/${programme.chid.replace(/ /g, '_')}/day_${index + 1}`}
                className={`content prog transparent-button ${isAllowed ? '' : 'blocked'}`}
                onClick={(e) => !isAllowed && e.preventDefault()}
                aria-label={`${item.title}, ${convertTime(item.length)}. ${item.description}`}
                aria-disabled={!isAllowed}
              >
                <div
                  className="content-img"
                  style={{ backgroundImage: `url(${item.thumbnail_image})` }}
                  aria-hidden="true"
                >
                  <FontAwesomeIcon icon={getIcon(item)} className="prog-icon" />
                </div>
                <div className="text" aria-hidden="true">
                  <p>{item.title}</p>
                  <p>{convertTime(item.length)}</p>
                  <p>{item.description}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="library prog" aria-labelledby="progress-section-heading">
        <h2 id="progress-section-heading">{t('M031')}</h2>
        <div className="prog-cont">
          <div className="progress-charts">
            {renderProgressGraph(faTimer, Number(programme?.progress || 0), 'M037', 'M038', 'M032')}
            {renderProgressGraph(
              faHourglassHalf,
              Number(programme?.minutes_watched || 0),
              'M039',
              'M040',
              'M033'
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
